import { AppConfirm } from "../../../utils/PopupUtil";
import TenantAPI from "../../api/TenantAPI";
import API from "../../api/API";

interface FetchTranscriptProps {
  audioBlob: Blob;
  onTranscriptSuccess: (transcript: string, fileData: any) => void;
  onError?: (fileData: any) => void;
  setFileData?: (data: any) => void;
  setRecordingURL?: (url: string | null) => void;
}

interface CancelTranscriptProps {
  fileData: any;
  setFileData?: any;
  afterDelete?: () => void;
  confirmation?: boolean;
}

interface DeleteTranscriptProps {
  fileData: any;
  afterDelete?: () => void;
}

const useTranscript = () => {
  async function fetchTranscript({
    audioBlob,
    onTranscriptSuccess,
    onError,
    setFileData,
    setRecordingURL,
  }: FetchTranscriptProps) {
    let fileResponse;

    try {
      // 1 - Upload audio file
      fileResponse = await TenantAPI.uploadFile({
        file: audioBlob,
        sources: undefined,
        usePublicContainer: false,
      });
      const fileName = fileResponse?.data?.data?.paths?.[0];

      if (!fileName) throw new Error("Failed to upload Audio file.");

      // 2 - Save file data in state incase user saves or cancels
      setFileData &&
        setFileData({ ...fileResponse.data.data, fileURL: fileName });

      // 3 - Grab audio file URL
      const fileURLResponse = await TenantAPI.getFileURL(fileName);
      const fileURL = fileURLResponse?.data?.data?.url;

      if (!fileURL) throw new Error("Failed to get File URL.");

      setRecordingURL && setRecordingURL(fileURL);

      // 4 - Send file URL to transcription endpoint
      const transcriptResponse = await API.getQuickTranscript({
        FileURL: fileURL,
      });

      // 5 - Proceed to preview page so user can view transcription
      const transcript = transcriptResponse.data.data?.transcription;

      onTranscriptSuccess(transcript || "", {
        ...fileResponse.data.data,
        fileURL: fileName,
      });

      return { ...fileResponse.data.data, fileURL: fileName };
    } catch (err) {
      AppConfirm({
        title: "Transcript Error!",
        text: "Something went wrong while creating your transcription. Please try again, and if the issue persists contact support.",
        showCancelButton: false,
        confirmButtonColor: "var(--primary)",
        confirmButtonText: "OK",
      });

      onError && onError(fileResponse?.data?.data);
      setFileData && setFileData(null);
    }
  }

  async function cancelTranscript({
    fileData,
    setFileData,
    afterDelete,
    confirmation = true,
  }: CancelTranscriptProps) {
    if (fileData?.recordID) {
      if (!confirmation) {
        TenantAPI.deleteFiles({
          type: "fileID",
          fileID: fileData.recordID,
          moduleAPIName: "",
          fieldAPIName: "",
          recordID: "",
        });
        return setFileData && setFileData(null);
      }

      const result = await AppConfirm({
        title: "Confirm Deletion",
        // @ts-ignore
        text: "Are you sure you want to delete your recording and transcription? This action cannot be undone.",
      });

      if (!result.isConfirmed) return true;

      TenantAPI.deleteFiles({
        type: "fileID",
        fileID: fileData.recordID,
        moduleAPIName: "",
        fieldAPIName: "",
        recordID: "",
      });
      setFileData && setFileData(null);
    }

    afterDelete && afterDelete();
  }

  async function deleteTranscript({
    fileData,
    afterDelete,
  }: DeleteTranscriptProps) {
    if (fileData?.recordID) {
      TenantAPI.deleteFiles({
        type: "fileID",
        fileID: fileData.recordID,
        moduleAPIName: "",
        fieldAPIName: "",
        recordID: "",
      });
    }

    afterDelete && afterDelete();
  }

  return { fetchTranscript, cancelTranscript, deleteTranscript };
};

export default useTranscript;
